import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
    Box,
    Button,
    Card,
    CardContent,
    Checkbox,
    Divider,
    FormControlLabel,
    Link,
    Typography,
} from '@mui/material';
import TextField from '../components/SecretTextField';
import { LoadingSpinner } from '../components/LoadingSpinner';
import GoogleSignInButton from '../components/GoogleSignInButton';
import WarningDialog from '../components/WarningDialog';
import { useAuth } from '../context/AuthContext';
import {
    googleComplete,
    registerComplete,
    registerStart,
    registerVerify,
    resendVerification,
} from '../services/AuthService';

import { getErrorMessage } from '../services/ApiError';
import { useCountdown } from '../hooks/useCountdown';

const Register = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const location = useLocation();
    const { applySession, isAuthenticated, loginWithGoogle } = useAuth();

    const googleTicketFromState = location.state?.googleTicket;
    const returnTo = location.state?.returnTo || '/';

    // Steps: 'email' -> 'code' -> 'profile'. Google users start at 'profile'.
    const [step, setStep] = useState(googleTicketFromState ? 'profile' : 'email');
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [registrationTicket, setRegistrationTicket] = useState('');
    const [googleTicket, setGoogleTicket] = useState(googleTicketFromState || '');
    const [is18Confirmed, setIs18Confirmed] = useState(false);
    const [username, setUsername] = useState('');
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);
    const completingProfile = useRef(false);
    const [resendCooldown, setResendCooldown] = useCountdown();

    useEffect(() => {
        if (location.state?.googleEmail) {
            setEmail(location.state.googleEmail);
        }
    }, [location.state]);

    useEffect(() => {
        if (isAuthenticated) {
            navigate(returnTo, { replace: true });
        }
    }, [isAuthenticated, navigate, returnTo]);


    const handleSendCode = async (event) => {
        event.preventDefault();
        if (!email.trim()) {
            setError(t('auth.errors.invalidEmail'));
            return;
        }
        if (!is18Confirmed) {
            setError(t('auth.errors.ageConfirmation'));
            return;
        }
        try {
            setLoading(true);
            setError('');
            const result = await registerStart(email.trim(), is18Confirmed);
            if (result?.registrationTicket) {
                setRegistrationTicket(result.registrationTicket);
                setInfo('');
                setStep('profile');
                return;
            }
            setStep('code');
            setResendCooldown(60);
            setInfo(t('auth.codeSent'));
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.registerStartFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleCredential = async (response) => {
        try {
            setLoading(true);
            setError('');
            setInfo('');
            const result = await loginWithGoogle(response.credential);
            if (result?.requiresProfile) {
                setGoogleTicket(result.googleTicket || '');
                setEmail(result.email || '');
                setStep('profile');
                return;
            }
            navigate(returnTo, { replace: true });
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.loginFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleResend = async () => {
        if (resendCooldown > 0) return;
        try {
            setLoading(true);
            setError('');
            await resendVerification(email.trim());
            setResendCooldown(60);
            setInfo(t('auth.codeSent'));
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.registerStartFailed')));
        } finally {
            setLoading(false);
        }
    };

    const handleVerify = async (event) => {
        event.preventDefault();
        if (!/^\d{6}$/.test(code.trim())) {
            setError(t('auth.errors.invalidCode'));
            return;
        }
        try {
            setLoading(true);
            setError('');
            const result = await registerVerify(email.trim(), code.trim());
            setRegistrationTicket(result?.registrationTicket || '');
            setStep('profile');
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.invalidCode')));
        } finally {
            setLoading(false);
        }
    };

    const handleCompleteProfile = async (event) => {
        event.preventDefault();
        if (completingProfile.current) return;
        setError('');

        if (!/^[A-Za-z0-9_-]{3,30}$/.test(username.trim())) {
            setError(t('auth.errors.invalidUsername'));
            return;
        }
        if (!googleTicket && !is18Confirmed) {
            setError(t('auth.errors.ageConfirmation'));
            return;
        }

        try {
            completingProfile.current = true;
            setLoading(true);
            if (googleTicket) {
                if (!is18Confirmed) {
                    setError(t('auth.errors.ageConfirmation'));
                    return;
                }
                const result = await googleComplete(googleTicket, is18Confirmed, username.trim());
                // google/complete returns AuthTokenResponse directly, unlike
                // google login's GoogleAuthResponse wrapper.
                if (!result?.accessToken || !result?.refreshToken || !result?.player) {
                    setError(t('auth.errors.registerCompleteFailed'));
                    return;
                }
                applySession(result);
                navigate(returnTo, { replace: true });
            } else {
                if (password !== confirmPassword) {
                    setError(t('auth.errors.passwordMismatch'));
                    return;
                }
                const result = await registerComplete(
                    registrationTicket,
                    username.trim(),
                    password,
                    confirmPassword
                );
                applySession(result);
                navigate(returnTo, { replace: true });
            }
        } catch (err) {
            setError(getErrorMessage(err, t('auth.errors.registerCompleteFailed')));
        } finally {
            completingProfile.current = false;
            setLoading(false);
        }
    };

    const renderEmailStep = () => (
        <form onSubmit={handleSendCode}>
            <TextField
                label={t('auth.email')}
                type="email"
                variant="outlined"
                fullWidth
                margin="normal"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
            />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={is18Confirmed}
                        onChange={(e) => setIs18Confirmed(e.target.checked)}
                        sx={{ color: '#e94560' }}
                    />
                }
                label={<Typography variant="body2">{t('auth.ageConfirm')}</Typography>}
            />
            <Button type="submit" className="app-btn" variant="contained" fullWidth sx={{ marginTop: 2 }}>
                {t('auth.sendVerificationCode')}
            </Button>
        </form>
    );

    const renderCodeStep = () => (
        <form onSubmit={handleVerify}>
            <Typography variant="body2" sx={{ marginBottom: 1 }}>
                {t('auth.codeSentTo', { email })}
            </Typography>
            <TextField
                label={t('auth.verificationCode')}
                type="password"
                variant="outlined"
                fullWidth
                margin="normal"
                inputProps={{ maxLength: 6, inputMode: 'numeric' }}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
            />
            <Button type="submit" className="app-btn" variant="contained" fullWidth sx={{ marginTop: 1 }}>
                {t('auth.verify')}
            </Button>
            <Button
                variant="text"
                fullWidth
                sx={{ marginTop: 1, color: '#e94560' }}
                onClick={handleResend}
                disabled={resendCooldown > 0}
            >
                {resendCooldown > 0
                    ? t('auth.resendIn', { seconds: resendCooldown })
                    : t('auth.resendCode')}
            </Button>
            <Button variant="text" fullWidth sx={{ color: '#aaa' }} onClick={() => setStep('email')}>
                {t('common.back')}
            </Button>
        </form>
    );

    const renderProfileStep = () => (
        <form onSubmit={handleCompleteProfile}>
            {googleTicket && (
                <Typography variant="body2" sx={{ marginBottom: 1 }}>
                    {t('auth.googleAccountFor', { email })}
                </Typography>
            )}
            <TextField label={t('auth.username')} variant="outlined" fullWidth margin="normal" required
                autoComplete="username" value={username} inputProps={{ maxLength: 30 }}
                onChange={(e) => setUsername(e.target.value)} />
            <FormControlLabel
                control={
                    <Checkbox
                        checked={is18Confirmed}
                        onChange={(e) => setIs18Confirmed(e.target.checked)}
                        sx={{ color: '#e94560' }}
                    />
                }
                label={<Typography variant="body2">{t('auth.ageConfirm')}</Typography>}
            />
            {!googleTicket && (
                <>
                    <TextField
                        label={t('auth.password')}
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                    />
                    <TextField
                        label={t('auth.confirmPassword')}
                        type="password"
                        variant="outlined"
                        fullWidth
                        margin="normal"
                        autoComplete="new-password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                    />
                </>
            )}
            <Button type="submit" className="app-btn" variant="contained" fullWidth disabled={loading} sx={{ marginTop: 2 }}>
                {loading ? t('common.loading') : t('auth.createAccount')}
            </Button>
        </form>
    );

    return (
        <Box className="app-page auth-page">
            <LoadingSpinner show={loading} />
            <Card className="app-surface auth-card" sx={{ width: { xs: '90%', sm: '25rem' }, backgroundColor: '#16213e', color: '#fff', padding: 2 }}>
                <CardContent>
                    <Typography className="app-page-title" variant="h5" align="center" sx={{ fontWeight: 'bold', marginBottom: 2, color: '#e94560' }}>
                        {t('auth.registerTitle')}
                    </Typography>
                    {info && (
                        <Typography variant="body2" sx={{ marginBottom: 1, color: '#8bd38b' }}>{info}</Typography>
                    )}
                    {step === 'email' && renderEmailStep()}
                    {step === 'code' && renderCodeStep()}
                    {step === 'profile' && renderProfileStep()}
                    {step === 'email' && (
                        <>
                            <Divider sx={{ my: 2, color: '#aaa' }}>{t('auth.or')}</Divider>
                            <GoogleSignInButton onCredential={handleGoogleCredential} />
                        </>
                    )}
                    <Box className="auth-links" sx={{ display: 'flex', justifyContent: 'center', marginTop: 2 }}>
                <Link component={RouterLink} to="/login" sx={{ color: '#e94560' }}>
                    {t('auth.haveAccount')}
                </Link>
            </Box>
        </CardContent>
    </Card>
    <WarningDialog
        fullWidth={true}
        open={!!error}
        onClose={() => setError('')}
        title={t('auth.registerTitle')}
        warningText={error}
    />
</Box>
    );
};

export default Register;

